// The one place the console states the contract-date ceiling, and the one rule
// that decides WHEN it is allowed to say it.
//
// Mirrors `infrastructure/entitlements/org_seat_provider.py :: MAX_CONTRACT_END_DATE`
// (`date(9000, 12, 31)`), which both organization writers apply as `le=` to both
// contract edges. It lives here rather than as a literal inside each mapper
// because there were two literals and the server deliberately has one — the
// Python constant's own instruction is that every path writing a contract edge
// reuses it, and the console was the one place that could drift from it. Not in
// `admin-orgs-types.ts`: that module's header forbids constants (D19), and the
// create form does not read it at all.
//
// WHY THE CLIENT HAS TO STATE THE BOUND AT ALL. Pydantic enforces `le=` on a
// `date` but does NOT emit it as `maximum`, so the ceiling is invisible on
// `/docs` and in anything generated from it; and neither date input carries a
// `max=`, deliberately (see `ExtendContractControl.tsx` — the server compares in
// UTC and a browser-local mirror disagrees with it by up to a day at the
// boundary). The 422 copy below is the only place an operator ever meets it.
export const MAX_CONTRACT_DATE_ISO = "9000-12-31";

/**
 * Pydantic's `type` for a `le=` refusal — the ONLY 422 that means "past the
 * ceiling".
 *
 * The discriminator reaches the client intact: `_scrub_validation_errors` drops
 * `ctx` and scrubs `input`, and touches nothing else in the error dict. Routing
 * on `type` rather than on `loc` alone is the whole point of this module — a
 * malformed date, a wrong type and an explicit null all arrive on the SAME field
 * (`date_from_datetime_parsing`, `date_from_datetime_inexact`, `date_type`), and
 * naming the ceiling at them sends the operator hunting a range problem that is
 * not there.
 *
 * NOT the server's English `msg`: prose is not an interface, and a copy edit on
 * the backend must not silently re-route a message here.
 */
export const CEILING_REFUSAL_TYPE = "less_than_equal";

/** True when this 422 detail item is the `le=` refusal and not some other date error. */
export function isCeilingRefusal(item: unknown): boolean {
  if (!item || typeof item !== "object") return false;
  return (item as { type?: unknown }).type === CEILING_REFUSAL_TYPE;
}

/**
 * The inline sentence for a 422 on one contract edge.
 *
 * Both mappers call THIS rather than composing their own copy, so the two
 * organization forms cannot describe one refusal two ways. `edge` names the
 * control the message lands on — a shared sentence that said "date" would be
 * routed correctly and still leave the operator guessing which of the two.
 */
export function contractDateMessage(edge: "start" | "end", item: unknown): string {
  return isCeilingRefusal(item)
    ? `Enter a contract ${edge} date on or before ${MAX_CONTRACT_DATE_ISO}.`
    : `Enter a valid contract ${edge} date.`;
}
